import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { ROListFormValuesT } from './declarations/index';
import useOpenROListPopup from './Hooks/useOpenROListPopup';
import validationSchema from './validationSchema';
import { postRepairOderListApi } from 'api/repairOrder';
import BaseModal from 'components/Modal/BaseModal';
import { MAX_RO_COUNT_TO_REQUEST, RO_REQUEST_TIP } from 'constants/repairOrder';
import { useResponsive } from 'contexts/ResponsiveContext';
import { TicketsRefinementKeysE } from 'declarations/enums/tickets';

const OpenROListPopup = () => {
  const { handleCloseROListPopup, isPopupOpened } = useOpenROListPopup();
  const fromMethods = useForm<ROListFormValuesT>({
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
  });

  const { setValue, getValues } = fromMethods;
  let [searchParams] = useSearchParams();
  const selectedRoofTopId = searchParams.get(TicketsRefinementKeysE.RoofTopId) ?? '';

  const [roValues, setRoValues] = useState('');
  const [roParsingResult, setRoParsingResult] = useState<{
    valid: string[];
    invalid: string[];
  } | null>(null);
  const { isMobile } = useResponsive();
  const handleChange = (event: any) => {
    setRoValues(event.target.value);
  };

  useEffect(() => {
    if (isPopupOpened && getValues('roofTopId') === undefined && selectedRoofTopId !== null) {
      setValue('roofTopId', selectedRoofTopId);
    }
  });

  useEffect(() => {
    if (isPopupOpened) {
      setRoValues('');
      setRoParsingResult(null);
    }
  }, [isPopupOpened]);

  const HandleROListParsing = () => {
    const RepairOrderListItems = roValues.split(',').map(token => token.trim());
    const validRepairOrderNumbers: string[] = [];
    const invalidRepairOrderNumbers: string[] = [];

    if (RepairOrderListItems.length <= MAX_RO_COUNT_TO_REQUEST) {
      for (const token of RepairOrderListItems) {
        if (token.match(/^\w+$/)) {
          validRepairOrderNumbers.push(token);
        } else {
          invalidRepairOrderNumbers.push(token);
        }
      }
      setRoParsingResult({
        valid: validRepairOrderNumbers,
        invalid: invalidRepairOrderNumbers,
      });
    } else {
      alert(`Please enter only up to ${MAX_RO_COUNT_TO_REQUEST} ROs`);
    }
  };

  const HandleClearROParsing = () => {
    setRoParsingResult(null);
  };

  const HandleSendListToWarrCloud = () => {
    if (roParsingResult?.valid) postRepairOderListApi(selectedRoofTopId, roParsingResult?.valid);
    handleCloseROListPopup();
  };

  return (
    <BaseModal
      open={isPopupOpened}
      size="xs"
      handleClose={handleCloseROListPopup}
      isMobile={isMobile}
    >
      <Box padding={4} height={isMobile ? '100%' : 300}>
        {!roParsingResult ? (
          <Box
            display="flex"
            flexDirection="column"
            width="100%"
            height="100%"
            alignItems="center"
            flexGrow={1}
          >
            <Box width="100%" flexGrow={1}>
              <TextareaAutosize
                style={{
                  width: '100%',
                  height: '70%',
                  padding: 4,
                  resize: 'none',
                  fontSize: '1rem',
                  border: '2px solid #ccc',
                  borderRadius: '4px',
                }}
                value={roValues}
                placeholder={RO_REQUEST_TIP}
                onChange={handleChange}
              />
            </Box>
            <Box display="flex" my={2} gap={4}>
              <Button size="medium" variant="outlined" onClick={handleCloseROListPopup}>
                Cancel
              </Button>
              <Button
                size="medium"
                variant="contained"
                color="primary"
                disabled={roValues.length <= 0}
                onClick={HandleROListParsing}
              >
                Ready
              </Button>
            </Box>
          </Box>
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            width="100%"
            height="100%"
            alignItems="center"
            flexGrow={1}
          >
            <Box
              display="flex"
              flexDirection="column"
              width="100%"
              height="100%"
              overflow="auto"
              flexGrow={1}
              mx={2}
              px={2}
              sx={{
                border: '2px solid #ccc',
                borderRadius: 2,
              }}
            >
              <Typography fontWeight="bold" mb={0.5}>
                Valid ROs:
              </Typography>
              {roParsingResult.valid.length > 0 ? (
                roParsingResult.valid.map((ro, index) => <li key={`valid-${index}`}>{ro}</li>)
              ) : (
                <Typography mb={0.5}>None</Typography>
              )}
              <br />
              <Typography fontWeight="bold" mb={0.5}>
                Invalid ROs:
              </Typography>
              {roParsingResult.invalid.length > 0 ? (
                roParsingResult.invalid.map((ro, index) => <li key={`invalid-${index}`}>{ro}</li>)
              ) : (
                <Typography mb={0.5}>None</Typography>
              )}
            </Box>
            <Box display="flex" my={2} gap={4}>
              <Button size="medium" variant="outlined" onClick={HandleClearROParsing}>
                Back
              </Button>
              <Button
                size="medium"
                variant="contained"
                color="primary"
                disabled={roParsingResult?.valid?.length <= 0}
                onClick={HandleSendListToWarrCloud}
              >
                Confirm
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </BaseModal>
  );
};
export default OpenROListPopup;
